/*
/joho1/dncl の中身 (表、例題、練習、間違い、出典)。頁の部品 (dncl.page.tsx) と試験 (dncl.test.ts) が同じ物を読む。
表の 1 行ずつは大学入試センターの 2 つの PDF (SOURCES) と照らしてある。例示に無い物は「例示に無い」と書き、埋めない。
例題と練習の Python の期待の出力は、dncl.test.ts が python3 で走らせて確かめる
*/

export const PAGE_PATH = "/joho1/dncl";
export const PAGE_URL = `https://yumesaku.tech${PAGE_PATH}`;

export const PAGE_META = {
  title: "DNCL と共通テスト用プログラム表記を Python で書いて動かす",
  seoTitle: "DNCL とは？共通テスト用プログラム表記と Python の対応表、例示の二分探索を動かす - ゆめさく",
  crumb: "DNCL",
  description:
    "DNCL は情報関係基礎で使われた旧い名前で、情報 I の共通テストは「共通テスト用プログラム表記」を使います。旧 DNCL、情報 I の表記、Python を 1 枚の表で並べ、大学入試センターの例示の二分探索を Python でその場で動かします。",
  author: "ゆめさく",
  published: "2026-11-04",
  updated: "2026-11-04",
  /** 一次の出典を取り直して照らした日 */
  checked: "2026-11-04",
} as const;

export type Source = { id: string; title: string; url: string; note: string };

export const SOURCES: readonly Source[] = [
  {
    id: "joho",
    title: "大学入試センター「概要『情報』」の「5. 共通テスト用プログラム表記の例示」",
    url: "https://www.dnc.ac.jp/albums/abm.php?d=744&f=abm00003141.pdf",
    note: "PDF の 18〜21 頁 (例示、二分探索、Python との対比)。PDF の更新日は 2022-11-09。情報 I の表記の正本で、二分探索の例と Python の記述例を含む",
  },
  {
    id: "dncl",
    title: "大学入試センター「共通テスト手順記述標準言語 (DNCL) の説明」",
    url: "https://www.dnc.ac.jp/albums/abm.php?d=666&f=abm00000819.pdf",
    note: "2022 年 1 月版、11 頁。情報関係基礎のための旧 DNCL",
  },
  {
    id: "r7",
    title: "令和 7 年度 大学入学共通テスト 本試験「情報 I」問題",
    url: "https://www.dnc.ac.jp/albums/abm.php?d=771&f=abm00005965.pdf&n=2025_op_43_joho1.pdf",
    note: "第 3 問の問 2 (問題冊子の 24 頁)。配列 Akibi の添字は 1 から始まると問題文に書いてある",
  },
  {
    id: "r8",
    title: "令和 8 年度 大学入学共通テスト 本試験「情報 I」問題",
    url: "https://www.dnc.ac.jp/albums/abm.php?d=2144&f=abm00017564.pdf&n=2026_ol_27_joho1.pdf",
    note: "第 3 問の問 2 (問題冊子の 26〜27 頁)。すべての配列の添字は 1 から始まると問題文に書いてあり、関数「要素数」と「最大値」は【関数の説明】の枠で説明される",
  },
];

export type Row = {
  topic: string;
  /** 旧 DNCL (2022 年 1 月版) */
  old: string;
  /** 情報 I の共通テスト用プログラム表記 (例示 2022-11-09) */
  joho: string;
  python: string;
  /** 表の下に添える短い説明 (無ければ空) */
  note?: string;
};

/** 表の中の「例示に無い」などの文はコードではないので、字の形で見分ける (頭が「〔」の物は地の文として出す) */
export const PLAIN = "〔";

export const ROWS: readonly Row[] = [
  { topic: "代入", old: "kosu ← 3", joho: "kosu = 3", python: "kosu = 3", note: "旧 DNCL の矢印が、情報 I では = に変わった" },
  {
    topic: "1 行に複数の代入",
    old: "kosu ← 3，kingaku ← 300",
    joho: "kosu = 3 , kingaku = 300",
    python: "kosu = 3; kingaku = 300",
  },
  { topic: "1 増やす", old: "kosu を 1 増やす", joho: "kosu = kosu + 1", python: "kosu += 1", note: "「増やす」の書き方は旧 DNCL だけにあり、情報 I の例示には無い" },
  { topic: "文字列", old: "「見つかりました」", joho: "\"I'll be back.\"", python: "\"I'll be back.\"", note: "旧 DNCL は「」も使える。情報 I はダブルクォーテーションで囲む" },
  { topic: "配列を作る", old: "Tokuten ← {87, 45, 72, 100}", joho: "Data = [10,20,30,40,50,60]", python: "Data = [10, 20, 30, 40, 50, 60]" },
  {
    topic: "全部の要素を 0 に",
    old: "Tokuten のすべての要素に 0 を代入する",
    joho: "Tokutenのすべての値を0にする",
    python: "Tokuten = [0] * len(Tokuten)",
  },
  { topic: "入力", old: "x ←【外部からの入力】", joho: "nyuryoku =【外部からの入力】", python: "nyuryoku = int(input())", note: "Python の input() は文字列を返すので、数として使うなら int() で包む" },
  {
    topic: "表示",
    old: "kosu と「個見つかった」を表示する",
    joho: "表示する(kosu,\"個見つかった\")",
    python: "print(kosu, \"個見つかった\")",
    note: "Python の print はカンマの所に空白を 1 つ入れる。令和 7 年度の本試験の表示例は値の間に空白が無かったので、空白は問題の表示例で確かめる",
  },
  { topic: "四則演算", old: "＋  −  ×  /", joho: "+  -  *  /", python: "+  -  *  /", note: "旧 DNCL のかけ算は ×" },
  { topic: "整数の商と余り", old: "7 ÷ 2  と  10 ％ 3", joho: "7 ÷ 2  と  10 ％ 3", python: "7 // 2  と  10 % 3", note: "どれも 3 と 1 になる。7 / 2 は 3.5" },
  { topic: "べき乗", old: "〔演算子は無い (問題文で関数を用意する)", joho: "2 ** 10", python: "2 ** 10" },
  { topic: "比較", old: "＝  ≠  ＞  ≧  ≦  ＜", joho: "==  !=  >  >=  <=  <", python: "==  !=  >  >=  <=  <", note: "旧 DNCL の ＝ は比べる記号で、代入ではない" },
  { topic: "論理", old: "かつ  または  でない", joho: "and  or  not", python: "and  or  not" },
  {
    topic: "条件分岐",
    old: "もし x ＜ 3 ならば\n  x ← x ＋ 1\nを実行し，そうでなければ\n  x ← x − 1\nを実行する",
    joho: "もし x < 3 ならば:\n｜ x = x + 1\nそうでなければ:\n⎿ x = x - 1",
    python: "if x < 3:\n    x = x + 1\nelse:\n    x = x - 1",
  },
  {
    topic: "条件を足す",
    old: "を実行し，そうでなくもし y ＞ 2 ならば",
    joho: "そうでなくもし x < 0 ならば:",
    python: "elif x < 0:",
  },
  {
    topic: "条件の間くり返す",
    old: "x ＜ 10 の間，\n  gokei ← gokei ＋ x\n  x ← x ＋ 1\nを繰り返す",
    joho: "n < 10 の間繰り返す:\n｜ goukei = goukei + n\n⎿ n = n + 1",
    python: "while n < 10:\n    goukei = goukei + n\n    n = n + 1",
  },
  {
    topic: "なるまでくり返す",
    old: "繰り返し，\n  gokei ← gokei ＋ x\n  x ← x ＋ 1\nを，x ≧ 10 になるまで実行する",
    joho: "〔例示に無い",
    python: "while True:\n    gokei = gokei + x\n    x = x + 1\n    if x >= 10:\n        break",
    note: "後で判定するので、中身は少なくとも 1 回は動く。Python に専用の文は無い",
  },
  {
    topic: "数を増やしながらくり返す",
    old: "x を 1 から 10 まで 1 ずつ増やしながら，\n  gokei ← gokei ＋ x\nを繰り返す",
    joho: "x を 0 から 9 まで 1 ずつ増やしながら繰り返す:\n⎿ goukei = goukei + Data[x]",
    python: "for x in range(0, 10):\n    goukei = goukei + Data[x]",
    note: "「9 まで」は 9 を含む。range の終わりは含まないので 1 足して 10 と書く",
  },
  {
    topic: "数を減らしながらくり返す",
    old: "x を 10 から 1 まで 1 ずつ減らしながら，\n  gokei ← gokei ＋ x\nを繰り返す",
    joho: "〔例示は「減らしながら」もあると書くだけ",
    python: "for x in range(10, 0, -1):\n    gokei = gokei + x",
  },
  {
    topic: "処理の範囲",
    old: "〔「を実行する」「を繰り返す」で閉じる",
    joho: "〔行の頭の ｜ で範囲を表し、⎿ で終わる",
    python: "〔字下げ (ふつうは空白 4 つ) で表す",
  },
  {
    topic: "添字の始まり",
    old: "〔0 以上。問題によっては 1 以上だけを使う",
    joho: "〔特に説明が無ければ 0 から",
    python: "〔いつも 0 から",
  },
  { topic: "2 次元の配列", old: "Gyoretu[3，2]", joho: "Data[2,4]", python: "Data[2][4]" },
  { topic: "要素の数", old: "〔説明書に無い (関数は問題文で用意する)", joho: "kazu = 要素数(Data)", python: "kazu = len(Data)" },
  {
    topic: "関数の定義",
    old: "関数 和を表示する(n) を\n  …\nと定義する",
    joho: "〔例示に無い (呼び出しだけが載っている)",
    python: "def show_sum(n):\n    ...",
  },
  { topic: "コメント", old: "〔説明書に無い", joho: "atai = 乱数()  #0以上1未満", python: "atai = random.random()  # 0 以上 1 未満", note: "# から行の終わりまでは動かない" },
];

export type Example = {
  id: string;
  title: string;
  /** 情報 I の表記 (行番号つき) */
  joho: string;
  python: string;
  /** python3 で走らせた出力。dncl.test.ts が照らす */
  expected: string;
};

/** 例示の二分探索。Python は例示の「Python3 記述例」から、入力の行を値に替え、字下げを空白 4 つにそろえた物 */
export const BINARY_SEARCH: Example = {
  id: "binary-search",
  title: "二分探索 (例示の 1 と 3)",
  joho: `( 1 ) Data=[3,18,29,33,48,52,62,77,89,97]
( 2 ) kazu=要素数(Data)
( 3 ) 表示する("0～99の数字を入力してください")
( 4 ) atai=【外部からの入力】
( 5 ) hidari=0 , migi=kazu-1
( 6 ) owari=0
( 7 ) hidari <= migi and owari==0 の間繰り返す:
( 8 ) ｜ aida=(hidari+migi)÷2   #演算子÷は商の整数値を返す
( 9 ) ｜ もし Data[aida]==atai ならば:
(10) ｜ ｜表示する(atai,"は",aida,"番目にありました")
(11) ｜ ｜owari=1
(12) ｜ そうでなくもし Data[aida]<atai ならば:
(13) ｜ ｜hidari=aida+1
(14) ｜ そうでなければ:
(15) ⎿ ⎿ migi=aida-1
(16) もし owari==0 ならば:
(17) ⎿ 表示する(atai,"は見つかりませんでした")
(18) 表示する("添字"," ","要素")
(19) iを0からkazu-1まで1ずつ増やしながら繰り返す:
(20) ⎿ 表示する(i,"        ",Data[i])`,
  python: `data = [3, 18, 29, 33, 48, 52, 62, 77, 89, 97]
kazu = len(data)
print('0～99の数字を入力してください')
atai = 52  # 試験では【外部からの入力】。85 に替えると見つからない道を通る
hidari = 0; migi = kazu - 1
owari = 0
while hidari <= migi and owari == 0:
    aida = (hidari + migi) // 2  # 演算子 // は商の整数部分
    if data[aida] == atai:
        print(atai, 'は', aida, '番目にありました')
        owari = 1
    elif data[aida] < atai:
        hidari = aida + 1
    else:
        migi = aida - 1
if owari == 0:
    print(atai, 'は見つかりませんでした')
print('添字', ' ', '要素')
for i in range(0, kazu, 1):
    print(i, '     ', data[i])
`,
  expected: `0～99の数字を入力してください
52 は 5 番目にありました
添字   要素
0       3
1       18
2       29
3       33
4       48
5       52
6       62
7       77
8       89
9       97`,
};

/** 演算子の違いを 1 度に見る小さな例。7 / 2、7 ÷ 2、10 ％ 3 は旧 DNCL の説明書の例と同じ数 */
export const OPERATORS: Example = {
  id: "operators",
  title: "割り算の 3 つとべき乗",
  joho: `表示する(7 / 2)
表示する(7 ÷ 2)
表示する(10 ％ 3)
表示する(2 ** 10)`,
  python: `print(7 / 2)
print(7 // 2)
print(10 % 3)
print(2 ** 10)
`,
  expected: `3.5
3
1
1024`,
};

export type Drill = {
  id: string;
  title: string;
  /** 読むときの手がかり (1 文) */
  hint: string;
  joho: string;
  starter: string;
  answer: string;
  expected: string;
};

export const DRILLS: readonly Drill[] = [
  {
    id: "sum",
    title: "1. 配列の合計",
    hint: "「0 から 3 まで」は 3 を含みます。range の終わりをいくつにするかが分かれ目です。",
    joho: `Data = [3, 5, 8, 2]
goukei = 0
x を 0 から 3 まで 1 ずつ増やしながら繰り返す:
⎿ goukei = goukei + Data[x]
表示する(goukei)`,
    starter: `Data = [3, 5, 8, 2]
goukei = 0
# ここに繰り返しを書く

print(goukei)
`,
    answer: `Data = [3, 5, 8, 2]
goukei = 0
for x in range(0, 4):
    goukei = goukei + Data[x]
print(goukei)
`,
    expected: "18",
  },
  {
    id: "div-mod",
    title: "2. 商と余りと条件分岐",
    hint: "÷ は //、％ は半角の % に置き換えます。｜ と ⎿ は字下げにします。",
    joho: `n = 47
表示する(n ÷ 10)
表示する(n ％ 10)
もし n ％ 2 == 0 ならば:
｜ 表示する("偶数")
そうでなければ:
⎿ 表示する("奇数")`,
    starter: `n = 47
# ここに Python を書く
`,
    answer: `n = 47
print(n // 10)
print(n % 10)
if n % 2 == 0:
    print("偶数")
else:
    print("奇数")
`,
    expected: "4\n7\n奇数",
  },
  {
    id: "one-based",
    title: "3. 添字が 1 から始まる配列",
    hint: "令和 7 年度と令和 8 年度の本試験と同じく、添字は 1 から始まると断ってあります。Python のリストは 0 から数えます。",
    joho: `# 配列 Ten の添字は 1 から始まる
Ten = [70, 85, 60]
i を 3 から 1 まで 1 ずつ減らしながら繰り返す:
⎿ 表示する(Ten[i])`,
    starter: `Ten = [70, 85, 60]
# ここに繰り返しを書く
`,
    answer: `Ten = [70, 85, 60]
for i in range(3, 0, -1):
    print(Ten[i - 1])
`,
    expected: "60\n85\n70",
  },
];

export type Pitfall = { title: string; body: string; wrong?: string; right?: string };

export const PITFALLS: readonly Pitfall[] = [
  {
    title: "添字が 0 からか 1 からか",
    body: "例示は、特に説明が無ければ配列の添字は 0 から始まると書いています。ところが令和 7 年度と令和 8 年度の本試験の情報 I では、配列の添字は 1 から始まると問題文で断っていました。Python に写すときは、添字から 1 を引くか、先頭に使わない要素を 1 つ置きます。",
    wrong: "print(Ten[3])   # IndexError",
    right: "print(Ten[3 - 1])",
  },
  {
    title: "「まで」は終わりを含む",
    body: "「x を 0 から 9 まで」は 9 も通ります。Python の range は終わりを含まないので、終わりに 1 を足します。減らしながらのときは 1 を引きます。",
    wrong: "for x in range(0, 9):",
    right: "for x in range(0, 10):",
  },
  {
    title: "÷ と / は別の割り算",
    body: "÷ は整数の商で、Python の // にあたります。/ は小数まで出す割り算で、7 / 2 は 3.5 です。二分探索の真ん中の添字を / で出すと、添字に小数が入って止まります。",
    wrong: "aida = (hidari + migi) / 2",
    right: "aida = (hidari + migi) // 2",
  },
  {
    title: "％ を全角のまま写す",
    body: "問題文の余りの記号は全角の ％ です。そのまま Python に貼ると、文字が違うという SyntaxError で止まります。半角の % に打ち直します。",
    wrong: "amari = n ％ 10",
    right: "amari = n % 10",
  },
  {
    title: "旧 DNCL の ＝ は比べる記号",
    body: "旧 DNCL では代入が ← で、＝ は「等しいか」を比べる記号でした。情報 I の表記と Python では = が代入、== が比較です。古い問題集で覚えた人ほど取り違えます。",
    wrong: "if kosu = 3:",
    right: "if kosu == 3:",
  },
  {
    title: "｜ と ⎿ を字下げに直し忘れる",
    body: "情報 I の表記は行の頭の ｜ と ⎿ で処理の範囲を表します。Python では ならば の後ろのコロンを残し、中の行を空白 4 つ下げます。⎿ の次の行で字下げを戻します。",
    wrong: "if x < 3:\nx = x + 1",
    right: "if x < 3:\n    x = x + 1",
  },
];

export type NextLink = { to: string; title: string; body: string };

export const NEXT_LINKS: readonly NextLink[] = [
  {
    to: "/learn/courses/python-basics",
    title: "Python の基礎を順に学ぶ",
    body: "変数、条件分岐、繰り返し、リストを、ブラウザで書いて動かしながら学ぶコースです。",
  },
  {
    to: "/luagate/path",
    title: "学習パスで次にやることを決める",
    body: "どの順で何を学ぶかを 1 本の道にした物です。開くにはログインが要ります。",
  },
  {
    to: "/computer-science",
    title: "探索と整列をもっと深く",
    body: "二分探索のような手順の考え方を、コンピュータサイエンスの道で読めます。",
  },
];

/** 判定のための比べ方。行の終わりの空白と、最後の空の行は見ない */
export function normalizeOutput(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+$/u, ""))
    .join("\n")
    .replace(/\n+$/u, "");
}
